import type { ChangeEvent } from "react";
import { useFormContext } from "react-hook-form";
import { Form } from "@circle-react-shared/uikit/Form";
import {
  type ButtonType,
  type ButtonVariant,
  generateFormItemName,
} from "../index";
import { CORNERS } from "./constants";
import type { ButtonRadiusSelectorType } from "./index";

interface InputsProps {
  buttonType: ButtonType;
  buttonVariant: ButtonVariant;
  selectedRadius: ButtonRadiusSelectorType;
}

type CornerTypes =
  | "border-left-top-radius"
  | "border-left-bottom-radius"
  | "border-right-top-radius"
  | "border-right-bottom-radius";

export const Inputs = ({
  buttonType,
  buttonVariant,
  selectedRadius,
}: InputsProps) => {
  const { setValue, getValues } = useFormContext();

  const getFieldName = (corner: CornerTypes) =>
    generateFormItemName(buttonType, buttonVariant, corner);

  const setBorderRadiiOnAllCorners = (value: string) => {
    CORNERS.forEach(corner => {
      setValue(getFieldName(corner), value, {
        shouldValidate: true,
        shouldDirty: true,
      });
    });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement>,
    corner: CornerTypes,
  ) => {
    const value = event.target.value;
    setValue(getFieldName(corner), value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  if (selectedRadius === "all") {
    const fieldName = getFieldName("border-left-top-radius");
    const value = getValues(fieldName) || "";

    return (
      <Form.Item
        isFirstChild
        hideBorder
        hideDescription
        name={fieldName}
        label={null}
      >
        <Form.Input
          label={null}
          type="number"
          className="border-primary bg-primary text-dark group-hover:border-darkest focus:border-darkest group-focus-within:border-darkest w-full appearance-none rounded-lg border px-3 py-2 text-center focus:!outline-none focus:!ring-0"
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setBorderRadiiOnAllCorners(event.target.value)
          }
          name={fieldName}
          value={value}
          min={0}
        />
      </Form.Item>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {CORNERS.map(corner => {
        const fieldName = getFieldName(corner);
        const value = getValues(fieldName) || "";

        return (
          <Form.Item
            key={corner}
            isFirstChild
            hideBorder
            hideDescription
            name={fieldName}
            label={null}
          >
            <Form.Input
              label={null}
              name={fieldName}
              value={value}
              type="number"
              className="border-primary bg-primary text-dark group-hover:border-darkest focus:border-darkest group-focus-within:border-darkest w-full appearance-none rounded-lg border px-3 py-2 text-center focus:!outline-none focus:!ring-0"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(event, corner)
              }
              min={0}
            />
          </Form.Item>
        );
      })}
    </div>
  );
};
